<template>
  <div class="ch-page">
    <!-- ── Hero ─────────────────────────────────────────── -->
    <div class="ch-hero">
      <div class="ch-hero__left">
        <div class="ch-hero__icon"><feather-icon icon="ArchiveIcon" size="22"/></div>
        <div>
          <h1 class="ch-hero__title">{{ msg('Cleaning history') }}</h1>
          <p class="ch-hero__subtitle">
            {{ tasks.length }} {{ tasks.length === 1 ? msg('record') : msg('records') }} {{ msg('shown') }}
          </p>
        </div>
      </div>
      <div class="ch-hero__stats">
        <div class="ch-stat ch-stat--cleaned">
          <div class="ch-stat__num">{{ cleanedCount }}</div>
          <div class="ch-stat__lbl">{{ msg('Cleaned') }}</div>
        </div>
        <div class="ch-stat ch-stat--damaged">
          <div class="ch-stat__num">{{ damagedCount }}</div>
          <div class="ch-stat__lbl">{{ msg('Damaged') }}</div>
        </div>
        <button class="ch-refresh" @click="refresh()" v-b-tooltip :title="msg('refresh')">
          <feather-icon icon="RefreshCcwIcon" size="16"/>
        </button>
      </div>
    </div>

    <!-- ── Filters — professional layout ──────────────────── -->
    <div class="ch-filters">
      <div class="ch-filters__head">
        <div class="ch-search">
          <feather-icon icon="SearchIcon" size="14" class="ch-search__icon"/>
          <input
              v-model="searchQuery"
              type="text"
              :placeholder="msg('Search by guest, address, cleaner…')"
              class="ch-search__input"
          />
          <button v-if="searchQuery" class="ch-search__clear" @click="searchQuery = ''">
            <feather-icon icon="XIcon" size="13"/>
          </button>
        </div>
        <div class="ch-toggles">
          <button
              class="ch-toggle ch-toggle--success"
              :class="{ active: filter.cleanedOnly }"
              @click="filter.cleanedOnly = !filter.cleanedOnly; refresh()"
          >
            <feather-icon :icon="filter.cleanedOnly ? 'CheckSquareIcon' : 'SquareIcon'" size="13" class="mr-25"/>
            {{ msg('Only Cleaned') }}
          </button>
          <button
              class="ch-toggle ch-toggle--danger"
              :class="{ active: filter.damagedOnly }"
              @click="filter.damagedOnly = !filter.damagedOnly; refresh()"
          >
            <feather-icon :icon="filter.damagedOnly ? 'CheckSquareIcon' : 'SquareIcon'" size="13" class="mr-25"/>
            {{ msg('Damaged') }}
          </button>
          <button v-if="hasActiveFilters" class="ch-clear-btn" @click="clearAllFilters">
            <feather-icon icon="XIcon" size="13" class="mr-25"/>
            {{ msg('Reset') }}
          </button>
        </div>
      </div>
      <div class="ch-filter-row">
        <div class="ch-filter-field">
          <label><feather-icon icon="CalendarIcon" size="13" class="mr-25"/>{{ msg('Date') }}</label>
          <calendar-picker-range v-model="filter.dateRange" @input="refresh"/>
        </div>
        <div class="ch-filter-field">
          <label><feather-icon icon="HomeIcon" size="13" class="mr-25"/>{{ msg('Building') }}</label>
          <building-select-box multiple v-model="filter.buildingIds" @input="refresh"/>
        </div>
        <div class="ch-filter-field">
          <label><feather-icon icon="LayersIcon" size="13" class="mr-25"/>{{ msg('Real estate') }}</label>
          <real-estate-picker multiple v-model="filter.realEstatesIds" @input="refresh"/>
        </div>
        <div class="ch-filter-field">
          <label><feather-icon icon="ActivityIcon" size="13" class="mr-25"/>{{ msg('Status') }}</label>
          <v-select ref="status-select" v-model="filter.statuses"
                    :reduce="(status) => status.value" label="label" :clearable="false"
                    :options="statusOptions" style="min-width: 150px; width: 100%">
            <template #option="option">
              <span :class="`text-${option.variant}`">{{ option.label }}</span>
            </template>
            <template #selected-option="option">
              <span :class="`text-${option.variant}`">{{ option.label }}</span>
            </template>
          </v-select>
        </div>
      </div>
    </div>

    <!-- ── Table card ───────────────────────────────────── -->
    <div class="ch-card">
      <div v-handy-scroll class="table-responsive ch-table-wrap">
        <b-table
            ref="plTable"
            class="ch-table"
            hover
            :items="items"
            :fields="fields"
            v-bind:busy="isLoading"
            @row-clicked="onItemSelected"
            primary-key="id"
        >
          <template #table-busy>
            <div class="text-center py-2">
              <b-spinner variant="primary" small class="mr-50"/>
              <span>{{ msg('Loading') }}…</span>
            </div>
          </template>

          <template #cell(realEstateId)="data">
            <a class="ch-cell-link" :href="`/real_estate/edit/${data.value}`" target="_blank" @click.stop>
              <feather-icon icon="HomeIcon" size="13" class="mr-25"/>
              <span v-if="data.value != null && realEstatesMap[data.value] != null">
                {{ realEstatesMap[data.value].fullAddress }}
              </span>
              <span v-else class="text-muted">{{ msg('Unknown') }}</span>
            </a>
          </template>

          <template #cell(contractId)="data">
            <a class="ch-cell-link" :href="`/real_estate/contracts/rent/${data.value}`" target="_blank" @click.stop>
              <feather-icon icon="UserIcon" size="13" class="mr-25"/>
              <span class="font-weight-bold">{{ data.item.clientName }}</span>
              <small class="text-muted ml-25">({{ data.item.requestedCleanTime | moment('DD/MM/YY HH:mm')}})</small>
            </a>
          </template>

          <template #cell(requestedCleanTime)="data">
            <span class="ch-time">{{ data.value | moment('DD/MM/YY HH:mm', true) }}</span>
          </template>

          <template #cell(requestedCleanTimeEnd)="data">
            <span v-if="data.value != null && data.value !== ''" class="ch-time">{{ data.value | moment('DD/MM/YY HH:mm', true) }}</span>
            <span v-else class="text-muted">—</span>
          </template>

          <template #cell(cleanedTime)="data">
            <span v-if="data.value != null && data.value !== ''" class="ch-time text-success">
              <feather-icon icon="CheckCircleIcon" size="12" class="mr-25"/>
              {{ data.value | moment('DD/MM/YY HH:mm', true) }}
            </span>
            <span v-else class="text-muted">—</span>
          </template>

          <template #cell(cleaner)="data">
            <span v-if="data.value" class="ch-cleaner">
              <feather-icon icon="UserIcon" size="11" class="mr-25"/>{{ data.value }}
            </span>
            <span v-else class="text-muted">—</span>
          </template>

          <template #cell(notes)="data">
            <span v-if="data.value" v-b-tooltip :title="data.value" class="ch-notes">
              {{ data.value | truncate(40) }}
            </span>
            <span v-else class="text-muted">—</span>
          </template>

          <template #cell(status)="data">
            <b-badge v-bind:variant="statusEnum[data.value].variant" pill class="ch-status">
              {{ statusEnum[data.value].label }}
            </b-badge>
          </template>
        </b-table>
      </div>

      <div v-if="!isLoading && items.length === 0" class="ch-empty">
        <feather-icon icon="ArchiveIcon" size="32" class="mb-50"/>
        <h4>{{ msg('No history found') }}</h4>
        <p>{{ msg('Try widening the date range or relaxing the filters.') }}</p>
      </div>

      <div v-if="pagination.total > pagination.size" class="ch-paginator">
        <b-pagination
            :disabled="isLoading"
            limit="11"
            :per-page="pagination.size"
            :current-page="pagination.page"
            :total-rows="pagination.total"
            align="center"
            @input="onPaginationChange"
        />
      </div>
    </div>

    <cleaning-modal ref="cleaningModal" @updated="refresh"/>
  </div>
</template>
<script>
import RealEstatePicker from "@/components/components/picker/realEstatesPicker.vue";
import {mapActions} from "vuex";
import cleaningModal from "@/components/modals/cleaning/cleaningModal.vue";
import buildingSelectBox from "@/components/components/picker/buildingSelectBox.vue";
import CalendarPickerRange from "@/components/widgets/calendar/calendarPickerRange.vue";

const getCleaningFilter = (start, end)=>({
  realEstatesIds: [],
  buildingIds: [],
  cleanedOnly: false,
  damagedOnly: false,
  statuses:[],
  dateRange:{ start, end }
});
export default {
  name: "CleaningTaskView",
  components: {CalendarPickerRange, RealEstatePicker,buildingSelectBox,cleaningModal},
  data: () => ({
    filter: getCleaningFilter(),
    tasks: [],
    realEstatesMap: {},
    isLoading: false,
    pagination: { page: 1, size: 10, total: 0, totalPages: 0 },
    searchQuery: '',
  }),
  filters: {
    truncate(value, len) {
      if (!value) return '';
      const s = String(value);
      return s.length > len ? s.slice(0, len) + '…' : s;
    }
  },
  created() {
    this.resetFilters();
    this.refresh()
    this.$root.$on('crm::branch-updated', this.refresh);
  },
  destroyed() {
    this.$root.$off('crm::branch-updated', this.refresh);
  },
  computed:{
    statusEnum() { return this.$t('cleaningStatusEnum'); },
    statusOptions() {
      return Object.keys(this.statusEnum).map(key => ({...this.statusEnum[key], value: key}));
    },
    items() {
      const q = (this.searchQuery || '').trim().toLowerCase();
      if (!q) return this.tasks;
      return this.tasks.filter(t => {
        const re = this.realEstatesMap[t.realEstateId] || {};
        return (
            (t.clientName && t.clientName.toLowerCase().includes(q))
            || (t.cleaner && t.cleaner.toLowerCase().includes(q))
            || (t.notes && t.notes.toLowerCase().includes(q))
            || (re.fullAddress && re.fullAddress.toLowerCase().includes(q))
        );
      });
    },
    cleanedCount() { return this.tasks.filter(t => t.status === 'CLEANED').length; },
    damagedCount() { return this.tasks.filter(t => t.hasDamage).length; },
    hasActiveFilters() {
      return (this.filter.buildingIds && this.filter.buildingIds.length > 0)
          || (this.filter.realEstatesIds && this.filter.realEstatesIds.length > 0)
          || (this.filter.statuses && this.filter.statuses.length > 0)
          || this.filter.cleanedOnly
          || this.filter.damagedOnly
          || (this.searchQuery && this.searchQuery.trim() !== '');
    },
    fields() {
      let $this = this
      return [
        {key: 'realEstateId', label: $this.msg('realEstate')},
        {key: 'contractId', label: $this.msg('Contract')},
        {key: 'requestedCleanTime', label: $this.msg('Requested') },
        {key: 'requestedCleanTimeEnd', label: $this.msg('End time') },
        {key: 'cleanedTime', label: $this.msg('Done at') },
        {key: 'cleaner', label: $this.msg('cleaner')},
        {key: 'notes', label: $this.msg('notes')},
        {key: 'status', label: $this.msg('Status')},
      ]
    },
  },
  methods: {
    ...mapActions('cleaning', ['fetchAllCleaningTasks']),
    refresh() {
      let $this = this;
      this.isLoading = true;
      this.fetchAllCleaningTasks({filter: this.filter}).then(data=>{
        this.tasks = data.CleaningTasks || [];
        this.realEstatesMap = (data.RealEstates || []).toObject('id');
        if (data.Total != null) this.pagination.total = data.Total;
        else if (data.TotalRows != null) this.pagination.total = data.TotalRows;
      }).catch(()=>{
        $this.$toast.error($this.msg('Error refreshing data'))
      }).finally(()=>{
        $this.isLoading = false;
      })
    },
    resetFilters() {
      this.filter = getCleaningFilter(this.$moment().startOf('week').format('YYYY-MM-DD'), this.$moment().endOf('day').format('YYYY-MM-DD'));
    },
    clearAllFilters() {
      this.resetFilters();
      this.searchQuery = '';
      this.refresh();
    },
    onPaginationChange(page) {
      this.pagination.page = page;
      this.refresh();
    },
    onItemSelected(task) {
      this.$refs.cleaningModal.showById(task.id);
    },
  }
}
</script>


<style scoped lang="scss">
$ch-radius: 14px;
$ch-radius-lg: 18px;
$ch-border: rgba(60, 60, 70, 0.08);
$ch-border-dark: rgba(255, 255, 255, 0.06);
$ch-primary: #7367f0;
$ch-success: #28c76f;
$ch-warning: #ff9f43;
$ch-danger: #ea5455;

.ch-page { min-height: 500px; padding-bottom: 24px; }

/* ── Hero ────────────────────────────────────────── */
.ch-hero {
  background: linear-gradient(135deg, rgba(115, 103, 240, 0.08) 0%, rgba(40, 199, 111, 0.04) 100%);
  border: 1px solid $ch-border;
  border-radius: $ch-radius-lg;
  padding: 18px 22px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 14px;
}

.ch-hero__left {
  display: flex;
  align-items: center;
  gap: 14px;
}

.ch-hero__icon {
  width: 48px;
  height: 48px;
  border-radius: 14px;
  background: linear-gradient(135deg, $ch-primary 0%, #5e50ee 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 22px rgba(115, 103, 240, 0.32);
}

.ch-hero__title {
  font-size: 1.5rem;
  font-weight: 800;
  margin: 0;
  letter-spacing: -0.02em;
  line-height: 1.2;
}

.ch-hero__subtitle {
  font-size: 0.85rem;
  color: var(--secondary);
  margin: 2px 0 0 0;
  font-weight: 500;
}

.ch-hero__stats {
  display: flex;
  align-items: center;
  gap: 10px;
}

.ch-stat {
  background: var(--card-bg, white);
  border: 1px solid $ch-border;
  border-radius: 12px;
  padding: 8px 14px;
  text-align: center;
  min-width: 84px;

  &--cleaned { border-color: rgba(40, 199, 111, 0.4); .ch-stat__num { color: $ch-success; } }
  &--damaged { border-color: rgba(234, 84, 85, 0.4); .ch-stat__num { color: $ch-danger; } }
}

.ch-stat__num { font-size: 1.2rem; font-weight: 800; letter-spacing: -0.02em; line-height: 1.1; }
.ch-stat__lbl { font-size: 0.65rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.08em; color: var(--secondary); margin-top: 2px; }

.ch-refresh {
  width: 40px;
  height: 40px;
  border: 1px solid $ch-border;
  border-radius: 12px;
  background: var(--card-bg, white);
  color: $ch-primary;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.18s ease;

  &:hover { background: $ch-primary; color: white; transform: rotate(180deg); }
}

/* ── Filters — professional layout ─────────────────────── */
.ch-filters {
  background: var(--card-bg, white);
  border: 1px solid $ch-border;
  border-radius: $ch-radius-lg;
  padding: 14px 16px;
  margin-bottom: 14px;
}

.ch-filters__head {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  padding-bottom: 12px;
  margin-bottom: 12px;
  border-bottom: 1px solid $ch-border;
}

.ch-search {
  flex: 1;
  min-width: 220px;
  display: flex;
  align-items: center;
  background: rgba(0, 0, 0, 0.03);
  border: 1px solid $ch-border;
  border-radius: 999px;
  padding: 0 12px;
  height: 36px;
  transition: all 0.18s ease;

  &:focus-within {
    border-color: $ch-primary;
    background: rgba(115, 103, 240, 0.04);
    box-shadow: 0 0 0 3px rgba(115, 103, 240, 0.12);
  }
}

.ch-search__icon { color: var(--secondary); flex-shrink: 0; margin-right: 8px; }

.ch-search__input {
  flex: 1;
  border: none;
  background: transparent;
  outline: none;
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--text);
  padding: 0;
  min-width: 0;

  &::placeholder { color: var(--secondary); opacity: 0.7; }
}

.ch-search__clear {
  width: 22px; height: 22px;
  border: none;
  background: rgba(0, 0, 0, 0.06);
  color: var(--secondary);
  border-radius: 50%;
  display: inline-flex; align-items: center; justify-content: center;
  cursor: pointer;
  flex-shrink: 0;
  margin-left: 4px;

  &:hover { background: $ch-danger; color: white; }
}

.ch-filter-row {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
}

.ch-filter-field label {
  display: flex;
  align-items: center;
  font-size: 0.68rem;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--secondary);
  margin-bottom: 6px;
}

.ch-toggles {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.ch-toggle {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 999px;
  border: 1px solid $ch-border;
  background: transparent;
  font-size: 0.78rem;
  font-weight: 700;
  color: var(--secondary);
  cursor: pointer;
  transition: all 0.18s ease;
  white-space: nowrap;

  &:hover { transform: translateY(-1px); }

  &--success {
    color: $ch-success;
    &.active { background: $ch-success; border-color: $ch-success; color: white; box-shadow: 0 4px 12px rgba(40, 199, 111, 0.32); }
  }
  &--danger {
    color: $ch-danger;
    &.active { background: $ch-danger; border-color: $ch-danger; color: white; box-shadow: 0 4px 12px rgba(234, 84, 85, 0.32); }
  }
}

.ch-clear-btn {
  display: inline-flex;
  align-items: center;
  padding: 6px 12px;
  border-radius: 999px;
  border: 1px dashed $ch-danger;
  background: transparent;
  font-size: 0.78rem;
  font-weight: 700;
  color: $ch-danger;
  cursor: pointer;
  transition: all 0.18s ease;

  &:hover { background: $ch-danger; color: white; }
}

/* ── Table ───────────────────────────────────────── */
.ch-card {
  background: var(--card-bg, white);
  border: 1px solid $ch-border;
  border-radius: $ch-radius-lg;
  overflow: hidden;
}

.ch-table-wrap { padding: 0; }

.ch-table ::v-deep {
  th {
    background: rgba(115, 103, 240, 0.06);
    font-size: 0.7rem;
    font-weight: 800;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: $ch-primary;
    border: none;
    padding: 12px 14px;
  }

  td {
    padding: 12px 14px;
    border-top: 1px solid $ch-border;
    vertical-align: middle;
    font-size: 0.85rem;
  }

  tbody tr {
    transition: background 0.15s ease;
    cursor: pointer;

    &:hover { background: rgba(115, 103, 240, 0.04); }
  }
}

.ch-cell-link {
  display: inline-flex;
  align-items: center;
  color: var(--text);
  text-decoration: none;
  font-weight: 500;

  &:hover { color: $ch-primary; }
}

.ch-time { font-size: 0.82rem; font-weight: 600; }

.ch-cleaner {
  display: inline-flex;
  align-items: center;
  font-size: 0.78rem;
  font-weight: 600;
  background: rgba(115, 103, 240, 0.08);
  color: $ch-primary;
  padding: 3px 8px;
  border-radius: 999px;
}

.ch-notes {
  font-size: 0.8rem;
  color: var(--secondary);
  font-style: italic;
}

.ch-status {
  font-size: 0.7rem;
  font-weight: 700;
  padding: 4px 10px;
}

.ch-empty {
  text-align: center;
  padding: 48px 20px;
  color: var(--secondary);

  h4 { font-weight: 700; margin: 8px 0 4px; font-size: 1rem; }
  p { font-size: 0.85rem; margin: 0; }
}

.ch-paginator {
  padding: 14px;
  border-top: 1px solid $ch-border;
}

/* ── Dark mode ───────────────────────────────────── */
.dark-layout {
  .ch-hero {
    background: linear-gradient(135deg, rgba(115, 103, 240, 0.18) 0%, rgba(40, 199, 111, 0.10) 100%);
    border-color: $ch-border-dark;
  }
  .ch-filters,
  .ch-card,
  .ch-stat,
  .ch-refresh,
  .ch-toggle,
  .ch-empty { border-color: $ch-border-dark; }
  .ch-stat,
  .ch-refresh,
  .ch-filters,
  .ch-card { background: #283046; }

  // Hero text
  .ch-hero__subtitle,
  .ch-stat__lbl,
  .ch-filter-field label { color: rgba(255, 255, 255, 0.55); }

  // Filter UI bar on dark
  .ch-filters__head { border-bottom-color: $ch-border-dark; }
  .ch-search {
    background: rgba(255, 255, 255, 0.04);
    border-color: $ch-border-dark;
    &:focus-within { background: rgba(115, 103, 240, 0.1); }
  }
  .ch-search__input { color: rgba(255, 255, 255, 0.95); }
  .ch-search__clear { background: rgba(255, 255, 255, 0.08); color: rgba(255, 255, 255, 0.6); }

  // Toggle pills
  .ch-toggle {
    background: rgba(255, 255, 255, 0.02);
    color: rgba(255, 255, 255, 0.6);
  }

  // Table
  .ch-table ::v-deep {
    th {
      background: rgba(115, 103, 240, 0.16);
      color: #b8b1ff;
    }
    td { border-top-color: $ch-border-dark; }
    tbody tr:hover { background: rgba(115, 103, 240, 0.10); }
  }

  .ch-paginator { border-top-color: $ch-border-dark; }
  .ch-cleaner {
    background: rgba(115, 103, 240, 0.16);
    color: #b8b1ff;
  }

  // Empty state
  .ch-empty {
    color: rgba(255, 255, 255, 0.5);
    h4 { color: rgba(255, 255, 255, 0.9); }
  }
}

@media (max-width: 700px) {
  .ch-hero { flex-direction: column; align-items: stretch; }
  .ch-hero__stats { justify-content: space-between; }
}
</style>
